import { useEffect, useRef, useState } from "react";
import { api, puede } from "../api.ts";
import { AvisoError, useAccion } from "../pantallas/comun.tsx";

interface Estado {
  activo: boolean; version?: string; titulo?: string; clausulas?: { titulo: string; texto: string }[];
  aceptado?: { en: string; por: string } | null; diasRestantes?: number; puedePosponer?: boolean;
}

/**
 * Contrato de encargo del tratamiento de datos (Donna encargada, la clínica responsable). Solo lo ve la administración y solo cuando el equipo de Donna lo activó.
 * Se lee y se acepta una vez por versión del texto; hasta entonces sale una tarjeta en Inicio. «Más tarde» se puede usar durante unos días; después la pantalla queda abierta.
 */
export function ContratoClinica() {
  const [e, setE] = useState<Estado | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [facultad, setFacultad] = useState(false);
  const a = useAccion();
  const cerrar = useRef<HTMLButtonElement>(null);
  const admin = puede("config:admin");
  useEffect(() => {
    if (!admin) return;
    api<Estado>("GET", "/api/contrato").then((r) => { setE(r); if (r.activo && !r.aceptado && !r.puedePosponer) setAbierto(true); }).catch(() => undefined);
  }, [admin]);
  useEffect(() => { if (abierto) cerrar.current?.focus(); }, [abierto]);
  if (!admin || !e || !e.activo || e.aceptado) return null;

  async function aceptar() {
    const r = await a.correr(() => api<unknown>("POST", "/api/contrato/aceptar", { version: e!.version, declaraFacultad: facultad }));
    if (r !== undefined) { setE({ ...e!, aceptado: { en: new Date().toISOString(), por: "" } }); setAbierto(false); }
  }

  return (
    <>
      <section className="card" aria-labelledby="t-contrato">
        <div className="fila" style={{ justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
          <h2 id="t-contrato" style={{ margin: 0 }}>Contrato de protección de datos</h2>
          <span className="chip rojo">{e.puedePosponer ? `Quedan ${e.diasRestantes} días para aceptarlo` : "Pendiente de aceptar"}</span>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Donna guarda los datos de sus pacientes por cuenta de su clínica. La ley pide que esa relación esté por escrito. Léelo y acéptalo: queda constancia de quién lo aceptó y cuándo.
        </p>
        <div><button className="btn pri" onClick={() => setAbierto(true)}>Leer y aceptar</button></div>
      </section>
      {abierto && (
        <div className="modal-fondo" role="dialog" aria-modal="true" aria-labelledby="t-contrato-texto" onKeyDown={(x) => { if (x.key === "Escape" && e.puedePosponer) setAbierto(false); }}>
          <section className="card aut-datos" style={{ maxHeight: "90vh", overflowY: "auto" }}>
            <div className="fila" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
              <h2 id="t-contrato-texto" style={{ margin: 0 }}>{e.titulo}</h2>
              {e.puedePosponer && <button ref={cerrar} className="btn chico" onClick={() => setAbierto(false)}>Más tarde</button>}
            </div>
            {e.clausulas?.map((c) => (
              <div key={c.titulo}><h3 style={{ margin: "10px 0 2px" }}>{c.titulo}</h3><p style={{ margin: "2px 0" }}>{c.texto}</p></div>
            ))}
            <label style={{ display: "block", marginTop: 12 }}>
              <input type="checkbox" checked={facultad} disabled={a.ocupado} onChange={(x) => setFacultad(x.target.checked)} />{" "}
              <b>Declaro que tengo facultad para representar a la clínica y acepto este contrato.</b>
            </label>
            <AvisoError error={a.error} />
            <div className="fila" style={{ flexWrap: "wrap", marginTop: 8 }}>
              <button className="btn pri" onClick={aceptar} disabled={a.ocupado || !facultad}>Aceptar el contrato</button>
            </div>
            <small className="muted">Versión {e.version}. Puedes pedir una copia escribiendo al equipo de Donna.</small>
          </section>
        </div>
      )}
    </>
  );
}
